import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { TrendingUp, Clock, Award, Book, Phone, AlertCircle, Upload, X, FileJson } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  
  useEffect(() => {
    fetchStats();
  }, []);
  
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'application/json') {
        setImportFile(file);
      } else {
        toast.error('Veuillez sélectionner un fichier JSON');
      }
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    setImporting(true);
    try {
      const fileContent = await importFile.text();
      const scenarios = JSON.parse(fileContent);

      if (!Array.isArray(scenarios)) {
        throw new Error('Le fichier doit contenir un tableau de scénarios');
      }

      const response = await axios.post(`${API}/scenarios/import`, { scenarios });
      
      toast.success(`${response.data.imported_count} scénarios importés avec succès!`);
      setShowImportModal(false);
      setImportFile(null);
      fetchStats();
    } catch (error) {
      console.error('Error importing scenarios:', error);
      toast.error(error.response?.data?.error || "Erreur lors de l'importation");
    } finally {
      setImporting(false);
    }
  };
  
  if (loading) {
    return (
      <div data-testid="dashboard-loading" className="flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-xl" style={{ color: '#2E6CF5' }}>Chargement...</div>
      </div>
    );
  }
  
  const statCards = [
    {
      title: 'Sessions Totales',
      value: stats?.total_sessions || 0,
      icon: Phone,
      gradient: 'linear-gradient(135deg, #002768, #2E6CF5)',
      testId: 'stat-total-sessions'
    },
    {
      title: 'Sessions Complétées',
      value: stats?.completed_sessions || 0,
      icon: TrendingUp,
      gradient: 'linear-gradient(135deg, #24B219, #82F486)',
      testId: 'stat-completed-sessions'
    },
    {
      title: 'Score Moyen',
      value: `${stats?.average_score || 0}/10`,
      icon: Award,
      gradient: 'linear-gradient(135deg, #2E6CF5, #CAE9FA)',
      testId: 'stat-average-score'
    },
    {
      title: "Heures d'Entraînement",
      value: `${stats?.total_training_hours || 0}h`,
      icon: Clock,
      gradient: 'linear-gradient(135deg, #002768, #24B219)',
      testId: 'stat-training-hours'
    },
  ];
  
  return (
    <div data-testid="dashboard-page" className="pt-20 px-4 sm:px-6 lg:px-8 pb-12 max-w-7xl mx-auto">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-2" style={{ color: '#002768' }}>Tableau de bord</h1>
          <p className="text-base text-gray-600">Vue d'ensemble de votre formation</p>
        </div>
        <button
          onClick={() => setShowImportModal(true)}
          className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
          style={{ background: 'linear-gradient(to right, #24B219, #82F486)' }}
          data-testid="import-scenarios-button"
        >
          <Upload className="w-5 h-5" />
          <span>Importer Scénarios</span>
        </button>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              data-testid={stat.testId}
              className="glass rounded-2xl p-6 hover:shadow-xl"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                     style={{ background: stat.gradient }}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="text-3xl font-bold mb-1" style={{ color: '#002768' }}>{stat.value}</div>
              <div className="text-sm text-gray-600">{stat.title}</div>
            </div>
          );
        })}
      </div>
      
      {/* Scenarios by Category */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card data-testid="scenarios-by-category" className="glass border-0">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Book className="w-5 h-5" style={{ color: '#002768' }} />
              <span>Scénarios par Catégorie</span>
            </CardTitle>
            <CardDescription>Distribution des scénarios disponibles</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.scenarios_by_category && Object.entries(stats.scenarios_by_category).map(([category, count]) => {
                const colors = {
                  police: '#2E6CF5',
                  ambulance: '#24B219',
                  incendie: '#DC2626'
                };
                return (
                  <div key={category} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors[category] || '#DADADA' }}></div>
                      <span className="text-gray-700 capitalize font-medium">{category}</span>
                    </div>
                    <span className="text-2xl font-bold" style={{ color: '#002768' }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        
        {/* Recent Sessions */}
        <Card data-testid="recent-sessions" className="glass border-0">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5" style={{ color: '#002768' }} />
              <span>Sessions Récentes</span>
            </CardTitle>
            <CardDescription>Dernières sessions d'entraînement</CardDescription>
          </CardHeader>
          <CardContent>
            {stats?.recent_sessions && stats.recent_sessions.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_sessions.map((session, index) => (
                  <div key={session.id || index} className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                    <div>
                      <div className="font-medium" style={{ color: '#002768' }}>{session.operator_name}</div>
                      <div className="text-xs text-gray-500">
                        {new Date(session.start_time).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {session.evaluation_score && (
                        <div className="text-lg font-bold" style={{ color: '#2E6CF5' }}>{session.evaluation_score}/10</div>
                      )}
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        session.status === 'completed' ? 'text-white' :
                        session.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-700'
                      }`} style={session.status === 'completed' ? { backgroundColor: '#24B219' } : {}}>
                        {session.status === 'completed' ? 'Terminé' :
                         session.status === 'in_progress' ? 'En cours' : 'Abandonné'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                Aucune session récente
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Quick Start CTA */}
      <div className="glass rounded-2xl p-8 text-center">
        <h2 className="text-2xl font-bold mb-3" style={{ color: '#002768' }}>Commencer un Nouvel Entraînement</h2>
        <p className="text-gray-600 mb-6">Choisissez un scénario d'urgence et commencez votre session de formation vocale</p>
        <a
          href="/scenarios"
          data-testid="start-training-button"
          className="inline-flex items-center space-x-2 px-8 py-4 text-white rounded-xl font-semibold hover:shadow-lg"
          style={{ background: 'linear-gradient(to right, #002768, #2E6CF5)' }}
        >
          <Phone className="w-5 h-5" />
          <span>Voir les Scénarios</span>
        </a>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold" style={{ color: '#002768' }}>Importer des Scénarios</h2>
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                  }}
                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fichier JSON de scénarios
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#2E6CF5] transition-colors">
                  <FileJson className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleFileChange}
                    className="hidden"
                    id="scenario-file"
                  />
                  <label
                    htmlFor="scenario-file"
                    className="cursor-pointer font-medium hover:underline"
                    style={{ color: '#2E6CF5' }}
                  >
                    Cliquez pour sélectionner un fichier
                  </label>
                  <p className="text-sm text-gray-500 mt-2">Format JSON uniquement</p>
                  {importFile && (
                    <div className="mt-3 inline-flex items-center space-x-2 px-3 py-1 rounded-full text-sm"
                         style={{ backgroundColor: '#CAE9FA', color: '#002768' }}>
                      <FileJson className="w-4 h-4" />
                      <span>{importFile.name}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-lg p-4 mb-6" style={{ backgroundColor: '#CAE9FA', border: '1px solid #2E6CF5' }}>
                <h3 className="font-semibold mb-2" style={{ color: '#002768' }}>Format attendu:</h3>
                <pre className="text-xs bg-white p-3 rounded overflow-x-auto" style={{ color: '#002768' }}>
{`[
  {
    "title": "Titre du scénario",
    "description": "Description de la situation",
    "category": "police|ambulance|incendie",
    "location": "Adresse complète",
    "victim_info": "Informations sur la victime",
    "instructions": "Instructions pour les secours",
    "severity": "low|medium|high|critical"
  }
]`}
                </pre>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                  }}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
                  disabled={importing}
                >
                  Annuler
                </button>
                <button
                  onClick={handleImport}
                  disabled={!importFile || importing}
                  className="flex-1 py-3 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(to right, #24B219, #82F486)' }}
                >
                  {importing ? 'Importation...' : 'Importer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
